import React from "react";
// Fixed: Added Type to the lucide-react imports to resolve the reference error on line 116.
import {
  Table as TableIcon,
  Video,
  Image as ImageIcon,
  ChevronDown,
  GripVertical,
  Trash2,
  Type,
  Backpack,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import RichTextEditor from "./RichTextEditor";
import TableEditor from "./TableEditor";
import MediaManager from "./MediaManager";
import { PropertySelector } from "./PropertySelector";
import { BlogSelector } from "./BlogSelector";

const SortableBlock = ({ block, onUpdate, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : 0,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group/block relative flex gap-4 ${isDragging ? "opacity-50" : ""}`}
    >
      <div className="flex flex-col gap-2 pt-2 opacity-0 group-hover/block:opacity-100 transition-opacity">
        <div
          {...attributes}
          {...listeners}
          className="p-1.5 text-slate-300 hover:text-indigo-500 bg-white border border-slate-200 rounded cursor-grab active:cursor-grabbing shadow-sm"
        >
          <GripVertical size={14} />
        </div>
        <button
          type="button"
          onClick={onDelete}
          className="p-1.5 text-slate-300 hover:text-red-500 bg-white border border-slate-200 rounded shadow-sm"
        >
          <Trash2 size={14} />
        </button>
      </div>

      <div className="flex-1 min-w-0">
        {block.type === "rich-text" && (
          <RichTextEditor
            value={block.content || ""}
            onChange={(val) => onUpdate({ content: val })}
          />
        )}
        {block.type === "table" && (
          <TableEditor
            config={block.table}
            onUpdate={(u) => onUpdate({ table: { ...block.table, ...u } })}
            onDelete={onDelete}
          />
        )}
        {block.type === "media" && (
          <MediaManager
            asset={block.media}
            onUpdate={(u) => onUpdate({ media: { ...block.media, ...u } })}
            onDelete={onDelete}
          />
        )}
        {block.type === "property-selector" && (
          <PropertySelector
            value={block.values || []}
            onChange={(val) => onUpdate({ values: val })}
          />
        )}
        {block.type === "blog-selector" && (
          <BlogSelector
            value={block.values || []}
            onChange={(val) => onUpdate({ values: val })}
          />
        )}
        {block.type === "custom-html" && (
          <div className="border border-slate-200 rounded-lg p-3 bg-white">
            <label className="text-xs font-semibold text-slate-500 mb-2 block">
              Custom HTML
            </label>
            <textarea
              value={block.html || ""}
              onChange={(e) => onUpdate({ html: e.target.value })}
              placeholder="<div>Your custom HTML here...</div>"
              className="w-full min-h-[150px] p-2 text-sm font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>
        )}

        {block.type === "cta" && (
          <div className="border border-slate-200 rounded-lg p-4 bg-white space-y-3">
            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">
                Button Text
              </label>
              <input
                type="text"
                value={block.cta?.label || ""}
                onChange={(e) =>
                  onUpdate({
                    cta: { ...block.cta, label: e.target.value },
                  })
                }
                maxLength={25}
                placeholder="e.g. Buy Now"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 block mb-1">
                Link
              </label>
              <input
                type="url"
                value={block.cta?.link || ""}
                onChange={(e) =>
                  onUpdate({
                    cta: { ...block.cta, link: e.target.value },
                  })
                }
                placeholder="https://example.com"
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="newTab"
                type="checkbox"
                checked={block.cta?.newTab || false}
                onChange={(e) =>
                  onUpdate({
                    cta: { ...block.cta, newTab: e.target.checked },
                  })
                }
                style={{ cursor: "pointer" }}
              />
              <label
                htmlFor="newTab"
                className="text-xs text-slate-500"
                style={{ cursor: "pointer" }}
              >
                Open in new tab
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ContentSectionEditor = ({ section, onUpdate }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addBlock = (type, subtype) => {
    let newBlock = { id: crypto.randomUUID(), type };
    if (type === "rich-text") newBlock.content = "";
    if (type === "custom-html") {
      newBlock.html = "";
    }
    if (type === "table") {
      newBlock.table = {
        rows: 2,
        cols: 2,
        hasHeaderRow: true,
        hasHeaderCol: false,
        hasFooterRow: false,
        stylePreset: "bordered",
        mobileBehavior: "scroll",
        data: Array(2)
          .fill(null)
          .map(() =>
            Array(2)
              .fill(null)
              .map(() => ({
                content: "",
                subtext: "",
                isHeader: false,
              })),
          ),
      };
    }
    if (type === "media") {
      newBlock.media = {
        id: crypto.randomUUID(),
        type: subtype || "image",
        url: "",
        altText: subtype === "image" ? "" : undefined,
        duration: 0,
        description: "",
      };
    }
    if (type === "cta") {
      newBlock.cta = {
        label: "",
        link: "",
        newTab: true,
      };
    }
    onUpdate({ blocks: [...section.blocks, newBlock] });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = section.blocks.findIndex((i) => i.id === active.id);
      const newIndex = section.blocks.findIndex((i) => i.id === over.id);
      onUpdate({ blocks: arrayMove(section.blocks, oldIndex, newIndex) });
    }
  };

  return (
    <section className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all">
      <div className="border-b border-slate-100 p-6 bg-slate-50/50">
        <div className="flex items-center gap-4 mb-4">
          <div className="relative">
            <select
              value={section.headingTag}
              onChange={(e) => onUpdate({ headingTag: e.target.value })}
              className="appearance-none bg-white border border-slate-200 pl-3 pr-8 py-1.5 rounded-lg text-sm font-bold text-slate-600 focus:outline-none"
            >
              <option value="h1">H1</option>
              <option value="h2">H2</option>
              <option value="h3">H3</option>
              <option value="h4">H4</option>
              <option value="h5">H5</option>
            </select>
            <ChevronDown className="w-3 h-3 absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
          <input
            type="text"
            placeholder="Section Heading..."
            className="flex-1 bg-transparent border-none text-2xl font-bold text-slate-800 placeholder:text-slate-200 focus:ring-0"
            value={section.title}
            onChange={(e) => onUpdate({ title: e.target.value })}
          />
        </div>

        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => addBlock("rich-text")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <Type className="w-3.5 h-3.5" /> Text
          </button>
          <button
            type="button"
            onClick={() => addBlock("table")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <TableIcon className="w-3.5 h-3.5" /> Table
          </button>
          <button
            type="button"
            onClick={() => addBlock("media", "image")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Image
          </button>
          <button
            type="button"
            onClick={() => addBlock("media", "video")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <Video className="w-3.5 h-3.5" /> Video
          </button>

          <button
            type="button"
            onClick={() => addBlock("property-selector")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <Backpack className="w-3.5 h-3.5" /> Property
          </button>

          <button
            type="button"
            onClick={() => addBlock("blog-selector")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <Backpack className="w-3.5 h-3.5" /> Blog
          </button>

          <button
            type="button"
            onClick={() => addBlock("custom-html")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <Backpack className="w-3.5 h-3.5" /> Custom HTML
          </button>

          <button
            type="button"
            onClick={() => addBlock("cta")}
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-md hover:border-primary hover:text-primary transition-colors"
          >
            <Backpack className="w-3.5 h-3.5" /> CTA Button
          </button>
        </div>
      </div>

      <div className="p-8">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={section.blocks.map((b) => b.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-8">
              {section.blocks.map((block) => (
                <SortableBlock
                  key={block.id}
                  block={block}
                  onUpdate={(u) => {
                    const idx = section.blocks.findIndex(
                      (b) => b.id === block.id,
                    );
                    const newBlocks = [...section.blocks];
                    newBlocks[idx] = {
                      ...newBlocks[idx],
                      ...u,
                    };
                    onUpdate({ blocks: newBlocks });
                  }}
                  onDelete={() =>
                    onUpdate({
                      blocks: section.blocks.filter((b) => b.id !== block.id),
                    })
                  }
                />
              ))}
              {section.blocks.length === 0 && (
                <div className="py-12 border-2 border-dashed border-slate-100 rounded-xl text-center text-slate-300 text-sm italic">
                  No content blocks yet. Add one above.
                </div>
              )}
            </div>
          </SortableContext>
        </DndContext>
      </div>
    </section>
  );
};

export default ContentSectionEditor;
